<template>
  <tr>
    <td class="product-info">
      <button
        class="btn btn-link btn-xs item-link delete-item"
        v-if="can_delete"
        @click.prevent.stop="deleting = true"
      >
        <i class="fas fa-close fa-lg"></i>
      </button>

      <delete-item-confirmation
        :open.sync="deleting"
        :item="item"
      ></delete-item-confirmation>

      <span class="brand">{{ product.brand }}</span>
      <h4>{{ product.name }}</h4>
      <span v-if="grouped">Order {{ order.number }}</span>
      <code v-else>{{ product.upc_13 }}</code>
    </td>
    <td>
      <table class="table table-condensed table-striped small preferences">
        <tr>
          <th>Quantity</th>
          <td>
            {{ item.quantity | fractional }}
            {{ product.unit_of_measure | unit-of-measure item.quantity true }}
          </td>
        </tr>
        <tr v-for="preference in preferences">
          <th>{{ preference.name }}</th>
          <td>{{ preference.value }}</td>
        </tr>
        <tr v-if="item.notes">
          <th>Notes</th>
          <td>{{ item.notes }}</td>
        </tr>
      </table>
    </td>
    <td class="text-center toggle">
      <button
        type="button"
        @click="toggleProcessed()"
        :class="{ 'btn-success': processed, 'btn-default': !processed }"
        class="btn btn-lg btn-toggle"
        :disabled="!in_stock || order_complete"
      >
        <i class="fas fa-check"></i>
      </button>
    </td>
    <td class="text-center toggle">
      <button
        type="button"
        @click="toggleCompleted()"
        :class="{ 'btn-success': completed, 'btn-default': !completed }"
        class="btn btn-lg btn-toggle"
        :disabled="!in_stock || order_complete"
      >
        <i class="fas fa-check"></i>
      </button>
    </td>
    <td class="text-center toggle">
      <button
        type="button"
        @click="toggleStockStatus()"
        :class="{ 'btn-success': !in_stock, 'btn-default': in_stock }"
        class="btn btn-lg btn-toggle"
      >
        <i class="fas fa-check"></i>
      </button>
    </td>
  </tr>
</template>

<script>
import Stash from 'src/stash';
import moment from 'moment';
import { has, get, pick } from 'lodash';
import DeleteItemConfirmation from 'modals/DeleteItemConfirmation';
import { Api, IO, updateProduct } from 'src/utilities';
import TogglesStockStatus from 'mixins/TogglesStockStatus';

let socketListeners = [];

export default {
  mixins: [TogglesStockStatus],

  props: [
    {
      name: 'item',
      type: Object,
    },
    {
      name: 'grouped',
      type: Boolean,
      default: false,
    },
  ],

  attached() {
    // attach socket.io listener for processed/completed toggle
    const itemListener = IO.on('item.modified', payload => {
      if (payload.id !== this.item.id) {
        return;
      }

      if (has(payload, 'processed_at')) {
        this.processed = payload.processed_at !== null;
      }

      if (has(payload, 'completed_at')) {
        this.completed = payload.completed_at !== null;
      }
    });

    // attach socket.io listener for product stock
    const productListener = IO.on('product.updated', payload => {
      if (payload.id !== this.product.id) {
        return;
      }

      if (has(payload, 'in_stock')) {
        this.product.in_stock = payload.in_stock;
      }
    });

    socketListeners = socketListeners
      .concat(itemListener)
      .concat(productListener);
  },

  detached() {
    console.log('DETACHING!');
    // detach each socket.io listener
    // socketListeners.forEach(listener => listener.destroy());
  },

  watch: {
    'product.in_stock'(v) {
      if (!v && this.processed) {
        this.toggleProcessed();
      }
    },
  },

  data() {
    return {
      deleting: false,
    };
  },

  computed: {
    can_delete() {
      if (this.order.completed_at != null) {
        return false;
      }

      return this.order.items.data.length > 1 && !this.grouped;
    },

    product() {
      return this.item.product;
    },

    order() {
      return Stash.getOrder(this.item.order_id);
    },

    order_complete() {
      return this.order.completed_at != null;
    },

    preferences() {
      return get(this.item, 'preferences.data', []);
    },

    processed: {
      get() {
        return this.item.processed_at !== null;
      },
      set(v) {
        this.item.processed_at = v
          ? moment().utc().format('YYYY-MM-DD HH:mm:ss')
          : null;

        if (this.item.processed_at === null) {
          this.completed = false;
        }
      },
    },

    completed: {
      get() {
        return this.item.completed_at !== null;
      },
      set(v) {
        this.item.completed_at = v
          ? moment().utc().format('YYYY-MM-DD HH:mm:ss')
          : null;

        if (this.item.completed_at !== null) {
          this.processed = true;
        }
      },
    },

    in_stock() {
      return this.product.in_stock;
    },
  },

  methods: {
    toggleProcessed() {
      const completed = this.completed;

      this.processed = !this.processed;

      const attrs = pick(this.item, ['order_id', 'id', 'processed_at']);

      // The socket.io emitter will only update all attrs
      if (completed && !this.processed) {
        attrs.completed_at = null;
      }

      // @api: update item's processed_at
      Api.put(`items/${this.item.id}/process`).catch(() => {
        if (completed) {
          this.completed = !this.completed;
        } else {
          this.processed = !this.processed;
        }
      });
    },

    toggleCompleted() {
      const processed = this.processed;

      this.completed = !this.completed;

      const attrs = pick(this.item, ['order_id', 'id', 'completed_at']);

      if (!processed && this.completed) {
        attrs.processed_at = this.item.processed_at;
      }

      // @api: update item's completed_at
      Api.put(`items/${this.item.id}/complete`).catch(() => {
        if (processed) {
          this.processed = !this.processed;
        } else {
          this.completed = !this.completed;
        }
      });
    },
  },

  components: { DeleteItemConfirmation },
};
</script>



// WEBPACK FOOTER //
// Item.vue?62883570